import React, { useState } from 'react';
import {
  ArrowLeft,
  Save,
  CheckCircle2,
  FolderTree,
  Compass,
  Heading,
  Repeat,
  ShieldCheck,
  CheckSquare,
  Plus,
  Trash2,
  ExternalLink,
  MapPin,
  Building2,
  Copy,
  Layers
} from 'lucide-react';
import {
  OnPageSeoProject,
  AuditInstance,
  KeywordResearchProject,
  OnPageCategoryItem,
  OnPageSubpageItem,
  ServiceContentItem,
  PermalinkAuditItem,
  OnPageChecklistReviewItem,
  PageBlueprintItem,
  SitePagesArchitecture
} from '../../types';
import {
  DEFAULT_ONPAGE_CHECKLIST,
  DEFAULT_PAGE_BLUEPRINTS,
  DEFAULT_TRUST_SIGNALS
} from '../../services/onPageStorageService';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';

interface OnPageSeoWorkspaceProps {
  project: OnPageSeoProject;
  audits?: AuditInstance[];
  kwProjects?: KeywordResearchProject[];
  onSaveProject: (updated: OnPageSeoProject) => void;
  onBackToDashboard: () => void;
}

type TabType = 'categories_menu' | 'location_strategy' | 'site_structure_pages' | 'content_hierarchy' | 'permalinks_redirects' | 'nap_gmb' | 'master_checklist';

export const OnPageSeoWorkspace: React.FC<OnPageSeoWorkspaceProps> = ({
  project: initialProject,
  onSaveProject,
  onBackToDashboard,
}) => {
  const [project, setProject] = useState<OnPageSeoProject>(initialProject);
  const [activeTab, setActiveTab] = useState<TabType>('categories_menu');
  const [saveToast, setSaveToast] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // New category inputs
  const [newCatName, setNewCatName] = useState('');
  const [newCatSlug, setNewCatSlug] = useState('');

  // New subpage inputs
  const [newSubpageTitle, setNewSubpageTitle] = useState('');
  const [newSubpageSlug, setNewSubpageSlug] = useState('');
  const [newSubpageCatId, setNewSubpageCatId] = useState('');
  const [newSubpageKeyword, setNewSubpageKeyword] = useState('');

  // New secondary service input for location plan
  const [newSrvName, setNewSrvName] = useState('');
  const [newSrvVolume, setNewSrvVolume] = useState<number>(250);
  const [newSrvKeyword, setNewSrvKeyword] = useState('');

  // New permalink audit input
  const [newPrmTitle, setNewPrmTitle] = useState('');
  const [newPrmOldUrl, setNewPrmOldUrl] = useState('');
  const [newPrmNewUrl, setNewPrmNewUrl] = useState('');

  // Site Structure & Pages to Build states
  const [pageCategoryFilter, setPageCategoryFilter] = useState<'all' | 'core' | 'service' | 'location' | 'trust_social' | 'authority' | 'legal'>('all');
  const [structureSubView, setStructureSubView] = useState<'pages' | 'trust_signals'>('pages');
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageUrl, setNewPageUrl] = useState('');
  const [newPageCategory, setNewPageCategory] = useState<'core' | 'service' | 'location' | 'trust_social' | 'authority' | 'legal'>('service');
  const [newPagePurpose, setNewPagePurpose] = useState('');

  // Fallback if project was saved before sitePagesArchitecture existed
  const sitePagesArchitecture: SitePagesArchitecture = project.sitePagesArchitecture || {
    totalPagesPlanned: DEFAULT_PAGE_BLUEPRINTS.length,
    blueprints: DEFAULT_PAGE_BLUEPRINTS,
    trustSignals: DEFAULT_TRUST_SIGNALS,
  };

  const handleTogglePagePlanned = (bpId: string) => {
    const updatedBps = sitePagesArchitecture.blueprints.map(bp => {
      if (bp.id === bpId) {
        return { ...bp, isPlanned: !bp.isPlanned };
      }
      return bp;
    });
    const updatedArch: SitePagesArchitecture = {
      ...sitePagesArchitecture,
      blueprints: updatedBps,
      totalPagesPlanned: updatedBps.filter(b => b.isPlanned).length,
    };
    handleUpdateProject({
      ...project,
      sitePagesArchitecture: updatedArch,
    });
  };

  const handleTogglePageBuilt = (bpId: string) => {
    const updatedBps = sitePagesArchitecture.blueprints.map(bp => {
      if (bp.id === bpId) {
        return { ...bp, isBuilt: !bp.isBuilt };
      }
      return bp;
    });
    const updatedArch: SitePagesArchitecture = {
      ...sitePagesArchitecture,
      blueprints: updatedBps,
    };
    handleUpdateProject({
      ...project,
      sitePagesArchitecture: updatedArch,
    });
  };

  const handleToggleTrustSignal = (tsId: string) => {
    const updatedTs = sitePagesArchitecture.trustSignals.map(ts => {
      if (ts.id === tsId) {
        return { ...ts, isImplemented: !ts.isImplemented };
      }
      return ts;
    });
    const updatedArch: SitePagesArchitecture = {
      ...sitePagesArchitecture,
      trustSignals: updatedTs,
    };
    handleUpdateProject({
      ...project,
      sitePagesArchitecture: updatedArch,
    });
  };

  const handleAddCustomPageBlueprint = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPageTitle.trim()) return;

    const newBp: PageBlueprintItem = {
      id: `bp-custom-${Date.now()}`,
      title: newPageTitle.trim(),
      targetUrl: newPageUrl.trim().startsWith('/') ? newPageUrl.trim() : `/${newPageUrl.trim()}`,
      category: newPageCategory,
      pageType: 'custom',
      isPlanned: true,
      isBuilt: false,
      purpose: newPagePurpose.trim() || 'Custom planned page for targeted search intent.',
      keyElements: ['Targeted H1', 'Internal links', 'Call to action', 'Global footer NAP'],
    };

    const updatedBps = [...sitePagesArchitecture.blueprints, newBp];
    const updatedArch: SitePagesArchitecture = {
      ...sitePagesArchitecture,
      blueprints: updatedBps,
      totalPagesPlanned: updatedBps.filter(b => b.isPlanned).length,
    };

    handleUpdateProject({
      ...project,
      sitePagesArchitecture: updatedArch,
    });

    setNewPageTitle('');
    setNewPageUrl('');
    setNewPagePurpose('');
    setShowAddPageModal(false);
  };

  const handleUpdateProject = (updated: OnPageSeoProject) => {
    setProject(updated);
    onSaveProject(updated);
    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 2000);
  };

  // Toggle checklist review item
  const handleToggleChecklistItem = (itemId: string) => {
    const mergedChecklist: Record<string, OnPageChecklistReviewItem> = {
      ...DEFAULT_ONPAGE_CHECKLIST,
      ...(project.checklist || {}),
    };

    const current = mergedChecklist[itemId] || {
      id: itemId,
      category: 'categories_menu',
      title: 'Review Item',
      description: '',
      isReviewed: false,
    };

    const updatedChecklist = {
      ...mergedChecklist,
      [itemId]: {
        ...current,
        isReviewed: !current.isReviewed,
        reviewedAt: !current.isReviewed ? new Date().toLocaleDateString() : undefined,
      },
    };

    const updated = {
      ...project,
      checklist: updatedChecklist,
      updatedAt: new Date().toISOString(),
    };
    handleUpdateProject(updated);
  };

  // Checklist counts (using merged checklist to ensure newly added items are accounted for)
  const mergedChecklist: Record<string, OnPageChecklistReviewItem> = {
    ...DEFAULT_ONPAGE_CHECKLIST,
    ...(project.checklist || {}),
  };
  const allChecklistItems = Object.values(mergedChecklist);
  const reviewedItemsCount = allChecklistItems.filter(i => i.isReviewed).length;
  const totalItemsCount = allChecklistItems.length;
  const progressPct = totalItemsCount > 0 ? Math.round((reviewedItemsCount / totalItemsCount) * 100) : 0;

  // Add category handler
  const handleAddCategory = () => {
    if (!newCatName.trim()) return;
    const slug = newCatSlug.trim() || newCatName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const newCat: OnPageCategoryItem = {
      id: `cat-${Date.now()}`,
      name: newCatName.trim(),
      slug,
      addedToMenu: true,
      isOneClickAway: true,
      notes: 'Added to primary navigation menu',
    };
    const updated: OnPageSeoProject = {
      ...project,
      categories: [...project.categories, newCat],
    };
    handleUpdateProject(updated);
    setNewCatName('');
    setNewCatSlug('');
  };

  // Delete category handler
  const handleDeleteCategory = (catId: string) => {
    const updated: OnPageSeoProject = {
      ...project,
      categories: project.categories.filter(c => c.id !== catId),
      subpages: project.subpages.filter(s => s.categoryId !== catId),
    };
    handleUpdateProject(updated);
  };

  // Add subpage handler
  const handleAddSubpage = () => {
    if (!newSubpageTitle.trim()) return;
    const slug = newSubpageSlug.trim() || newSubpageTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const newSub: OnPageSubpageItem = {
      id: `sub-${Date.now()}`,
      title: newSubpageTitle.trim(),
      slug,
      categoryId: newSubpageCatId || project.categories[0]?.id || 'cat-1',
      targetKeyword: newSubpageKeyword.trim() || newSubpageTitle.trim(),
      addedToMenu: true,
      isOneClickAway: true,
    };
    const updated: OnPageSeoProject = {
      ...project,
      subpages: [...project.subpages, newSub],
    };
    handleUpdateProject(updated);
    setNewSubpageTitle('');
    setNewSubpageSlug('');
    setNewSubpageKeyword('');
  };

  // Delete subpage handler
  const handleDeleteSubpage = (subId: string) => {
    const updated: OnPageSeoProject = {
      ...project,
      subpages: project.subpages.filter(s => s.id !== subId),
    };
    handleUpdateProject(updated);
  };

  // Add secondary service to Perth / main location plan
  const handleAddSecondaryService = () => {
    if (!newSrvName.trim()) return;
    const curPlan = project.locationPlans[0] || {
      targetCity: 'Perth',
      isPrimaryLocation: false,
      locationPageUrl: '/perth',
      h1Service: {
        id: 'srv-1',
        serviceName: 'Web Design',
        headingTag: 'H1',
        searchVolume: 500,
        targetKeyword: 'Web Design Perth',
        recommendedWordCount: 350,
        keywordOccurrencesInContent: 2,
      },
      secondaryServices: [],
    };

    const newSrv: ServiceContentItem = {
      id: `srv-${Date.now()}`,
      serviceName: newSrvName.trim(),
      headingTag: 'H2',
      searchVolume: newSrvVolume,
      targetKeyword: newSrvKeyword.trim() || `${newSrvName.trim()} ${curPlan.targetCity}`,
      recommendedWordCount: newSrvVolume >= 300 ? 300 : 220,
      actualWordCount: newSrvVolume >= 300 ? 300 : 220,
      keywordOccurrencesInContent: newSrvVolume >= 300 ? 2 : 1,
      notes: 'Secondary service with H2 heading tag.',
    };

    const updatedPlan = {
      ...curPlan,
      secondaryServices: [...curPlan.secondaryServices, newSrv],
    };

    const updated: OnPageSeoProject = {
      ...project,
      locationPlans: [updatedPlan, ...project.locationPlans.slice(1)],
    };
    handleUpdateProject(updated);
    setNewSrvName('');
    setNewSrvVolume(200);
    setNewSrvKeyword('');
  };

  // Add permalink audit item
  const handleAddPermalink = () => {
    if (!newPrmOldUrl.trim()) return;
    const newPrm: PermalinkAuditItem = {
      id: `prm-${Date.now()}`,
      pageTitle: newPrmTitle.trim() || 'Custom Page',
      oldUrl: newPrmOldUrl.trim(),
      newUrl: newPrmNewUrl.trim() || `${project.websiteUrl.replace(/\/$/, '')}/clean-url`,
      isLongOrMessy: true,
      decision: 'update_and_redirect',
      redirectType: '301',
      redirectTool: 'yoast',
      status: 'pending',
    };
    const updated: OnPageSeoProject = {
      ...project,
      permalinkAudits: [...project.permalinkAudits, newPrm],
    };
    handleUpdateProject(updated);
    setNewPrmTitle('');
    setNewPrmOldUrl('');
    setNewPrmNewUrl('');
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const currentPlan = project.locationPlans[0];

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', background: '#050912' }}>
      
      {/* 1. TOP APP WORKSPACE HEADER */}
      <div
        style={{
          background: '#070c16',
          borderBottom: '1px solid #1e293b',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          zIndex: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={onBackToDashboard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: '#0f172a',
              border: '1px solid #1e293b',
              borderRadius: '8px',
              padding: '6px 12px',
              color: '#94a3b8',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <ArrowLeft size={14} />
            <span>All Plans</span>
          </button>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 style={{ fontSize: '17px', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                {project.clientName}
              </h2>
              <Badge variant={progressPct === 100 ? 'complete' : 'current'}>
                {progressPct === 100 ? 'REVIEW COMPLETE' : 'IN PROGRESS'}
              </Badge>
              <span style={{ fontSize: '12px', color: '#64748b' }}>•</span>
              <a
                href={project.websiteUrl}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: '12px', color: '#38bdf8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span>{project.websiteUrl}</span>
                <ExternalLink size={12} />
              </a>
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
              Primary Location: <span style={{ color: '#ffffff', fontWeight: 600 }}>{project.primaryLocation}</span> · Model: <span style={{ color: '#ffffff', fontWeight: 600 }}>{project.locationModel === 'multi_location' ? 'Multi-Location Hub' : 'Single Location'}</span>
            </div>
          </div>
        </div>

        {/* Top Right Review Tracker & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Progress Bar Capsule */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#090e1a', border: '1px solid #1e293b', padding: '6px 14px', borderRadius: '10px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>
              Checklist: <span style={{ color: progressPct === 100 ? '#10b981' : '#38bdf8', fontFamily: 'var(--font-mono)' }}>{reviewedItemsCount}/{totalItemsCount}</span>
            </div>
            <div style={{ width: '80px', height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${progressPct}%`,
                  height: '100%',
                  background: progressPct === 100 ? '#10b981' : 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                  borderRadius: '3px',
                }}
              />
            </div>
            <span style={{ fontSize: '12px', fontWeight: 800, color: progressPct === 100 ? '#10b981' : '#ffffff', fontFamily: 'var(--font-mono)' }}>
              {progressPct}%
            </span>
          </div>

          <Button
            size="sm"
            variant={saveToast ? 'success' : 'primary'}
            icon={saveToast ? <CheckCircle2 size={14} /> : <Save size={14} />}
            onClick={() => handleUpdateProject(project)}
          >
            {saveToast ? 'Saved!' : 'Save Progress'}
          </Button>
        </div>
      </div>

      {/* 2. SUB-NAVIGATION WORKSTATION TABS */}
      <div
        style={{
          background: '#070c16',
          borderBottom: '1px solid #1e293b',
          padding: '0 24px',
          display: 'flex',
          gap: '4px',
          overflowX: 'auto',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setActiveTab('categories_menu')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: 'none',
            borderBottom: activeTab === 'categories_menu' ? '2px solid #38bdf8' : '2px solid transparent',
            background: 'none',
            color: activeTab === 'categories_menu' ? '#38bdf8' : '#94a3b8',
            fontSize: '12.5px',
            fontWeight: activeTab === 'categories_menu' ? 700 : 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <FolderTree size={15} />
          <span>1. Categories & 1-Click Menu</span>
        </button>

        <button
          onClick={() => setActiveTab('location_strategy')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: 'none',
            borderBottom: activeTab === 'location_strategy' ? '2px solid #38bdf8' : '2px solid transparent',
            background: 'none',
            color: activeTab === 'location_strategy' ? '#38bdf8' : '#94a3b8',
            fontSize: '12.5px',
            fontWeight: activeTab === 'location_strategy' ? 700 : 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Compass size={15} />
          <span>2. Single vs. Multi-Location</span>
        </button>

        <button
          onClick={() => setActiveTab('site_structure_pages')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: 'none',
            borderBottom: activeTab === 'site_structure_pages' ? '2px solid #38bdf8' : '2px solid transparent',
            background: 'none',
            color: activeTab === 'site_structure_pages' ? '#38bdf8' : '#94a3b8',
            fontSize: '12.5px',
            fontWeight: activeTab === 'site_structure_pages' ? 700 : 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Layers size={15} />
          <span>3. Pages to Build & Trust Signals</span>
        </button>

        <button
          onClick={() => setActiveTab('content_hierarchy')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: 'none',
            borderBottom: activeTab === 'content_hierarchy' ? '2px solid #38bdf8' : '2px solid transparent',
            background: 'none',
            color: activeTab === 'content_hierarchy' ? '#38bdf8' : '#94a3b8',
            fontSize: '12.5px',
            fontWeight: activeTab === 'content_hierarchy' ? 700 : 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Heading size={15} />
          <span>4. Heading & Content Scaling</span>
        </button>

        <button
          onClick={() => setActiveTab('permalinks_redirects')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: 'none',
            borderBottom: activeTab === 'permalinks_redirects' ? '2px solid #38bdf8' : '2px solid transparent',
            background: 'none',
            color: activeTab === 'permalinks_redirects' ? '#38bdf8' : '#94a3b8',
            fontSize: '12.5px',
            fontWeight: activeTab === 'permalinks_redirects' ? 700 : 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Repeat size={15} />
          <span>5. Permalinks & 301 Redirects</span>
        </button>

        <button
          onClick={() => setActiveTab('nap_gmb')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: 'none',
            borderBottom: activeTab === 'nap_gmb' ? '2px solid #38bdf8' : '2px solid transparent',
            background: 'none',
            color: activeTab === 'nap_gmb' ? '#38bdf8' : '#94a3b8',
            fontSize: '12.5px',
            fontWeight: activeTab === 'nap_gmb' ? 700 : 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <ShieldCheck size={15} />
          <span>6. NAP Consistency & GMB Trust</span>
        </button>

        <button
          onClick={() => setActiveTab('master_checklist')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: 'none',
            borderBottom: activeTab === 'master_checklist' ? '2px solid #10b981' : '2px solid transparent',
            background: 'none',
            color: activeTab === 'master_checklist' ? '#10b981' : '#94a3b8',
            fontSize: '12.5px',
            fontWeight: activeTab === 'master_checklist' ? 700 : 500,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <CheckSquare size={15} color={activeTab === 'master_checklist' ? '#10b981' : '#64748b'} />
          <span>7. Master Review Checklist ({reviewedItemsCount}/{totalItemsCount})</span>
        </button>
      </div>

      {/* 3. MAIN WORKSTATION CONTENT AREA */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px 80px 32px' }}>
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>

          {/* ============================================================
              TAB 1: WORDPRESS CATEGORIES & 1-CLICK NAVIGATION
             ============================================================ */}
          {activeTab === 'categories_menu' && (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Instructor Strategy Card */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.1) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '14px',
                  padding: '24px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    LECTURE 5 WORKFLOW · WORDPRESS ARCHITECTURE
                  </span>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
                  Category Creation, Menu Assignment & The 1-Click Away Rule
                </h3>
                <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: '0 0 14px 0', lineHeight: 1.6 }}>
                  The instructor demonstrates the exact sequence: (1) Navigate to <strong>Posts → Categories</strong> and create service categories; (2) Open <strong>Appearance → Menus</strong>, select the category, and add it to the primary navigation; (3) Create service subpages in <strong>Pages → Add New</strong>, publish them, and nest them under the category in the menu. All subpages must be maximum 1 click away from navigation.
                </p>

                {/* 3-Step Walkthrough Flow Badges */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div style={{ background: '#040711', border: '1px solid #1e293b', borderRadius: '10px', padding: '12px 16px' }}>
                    <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase' }}>STEP 1 · CMS CATEGORIES</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginTop: '3px' }}>Posts → Categories</div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>Define primary service categories</div>
                  </div>
                  <div style={{ background: '#040711', border: '1px solid #1e293b', borderRadius: '10px', padding: '12px 16px' }}>
                    <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase' }}>STEP 2 · MENU INTEGRATION</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginTop: '3px' }}>Appearance → Menus</div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>Add categories to primary menu</div>
                  </div>
                  <div style={{ background: '#040711', border: '1px solid #1e293b', borderRadius: '10px', padding: '12px 16px' }}>
                    <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#10b981', textTransform: 'uppercase' }}>STEP 3 · 1-CLICK AWAY RULE</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginTop: '3px' }}>Nest Pages Under Menus</div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>Maximum 1 click from homepage</div>
                  </div>
                </div>
              </div>

              {/* Service Categories Manager */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: '0 0 4px 0' }}>
                      Configured Service & Location Categories
                    </h4>
                    <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                      Categories registered in the CMS and exposed in primary navigation.
                    </p>
                  </div>
                </div>

                {/* Categories Table */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' }}>
                  {project.categories.map((cat) => (
                    <div
                      key={cat.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#040711',
                        border: '1px solid #1e293b',
                        borderRadius: '10px',
                        padding: '12px 18px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FolderTree size={18} color="#38bdf8" />
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>
                            {cat.name}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#64748b', fontFamily: 'var(--font-mono)' }}>
                            slug: /{cat.slug}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '3px 8px', borderRadius: '6px' }}>
                          ✓ In Primary Menu
                        </span>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.1)', padding: '3px 8px', borderRadius: '6px' }}>
                          ✓ 1-Click Away
                        </span>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}
                          title="Remove Category"
                        >
                          <Trash2 size={15} color="#ef4444" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Category Form */}
                <div style={{ display: 'flex', gap: '12px', background: '#040711', padding: '14px', borderRadius: '10px', border: '1px solid #1e293b' }}>
                  <input
                    type="text"
                    placeholder="New category name (e.g. Services, Locations)"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    style={{ flex: 2, background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  />
                  <input
                    type="text"
                    placeholder="Slug (optional, e.g. services)"
                    value={newCatSlug}
                    onChange={(e) => setNewCatSlug(e.target.value)}
                    style={{ flex: 1, background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  />
                  <Button size="sm" variant="primary" icon={<Plus size={14} />} onClick={handleAddCategory}>
                    Add Category
                  </Button>
                </div>
              </div>

              {/* Service Subpages & 1-Click Verification */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: '0 0 4px 0' }}>
                    Assigned Service Subpages (1-Click Away Architecture)
                  </h4>
                  <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                    Every service subpage must be directly reachable from the main menu dropdowns.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' }}>
                  {project.subpages.map((sub) => {
                    const parentCat = project.categories.find(c => c.id === sub.categoryId);
                    return (
                      <div
                        key={sub.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: '#040711',
                          border: '1px solid #1e293b',
                          borderRadius: '10px',
                          padding: '12px 18px',
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>{sub.title}</span>
                            <span style={{ fontSize: '11px', color: '#38bdf8', background: 'rgba(2, 132, 199, 0.12)', padding: '2px 8px', borderRadius: '4px' }}>
                              Under: {parentCat?.name || 'Category'}
                            </span>
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                            URL: /{parentCat?.slug || 'services'}/{sub.slug} · Keyword: {sub.targetKeyword}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '3px 8px', borderRadius: '6px' }}>
                            1-Click Verified
                          </span>
                          <button
                            onClick={() => handleDeleteSubpage(sub.id)}
                            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}
                            title="Remove Subpage"
                          >
                            <Trash2 size={15} color="#ef4444" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Subpage Form */}
                <div style={{ display: 'flex', gap: '12px', background: '#040711', padding: '14px', borderRadius: '10px', border: '1px solid #1e293b', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Page Title (e.g. Web Design Sydney)"
                    value={newSubpageTitle}
                    onChange={(e) => setNewSubpageTitle(e.target.value)}
                    style={{ flex: '1 1 200px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  />
                  <select
                    value={newSubpageCatId}
                    onChange={(e) => setNewSubpageCatId(e.target.value)}
                    style={{ flex: '1 1 150px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  >
                    {project.categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Target Keyword (e.g. Web Design Company Sydney)"
                    value={newSubpageKeyword}
                    onChange={(e) => setNewSubpageKeyword(e.target.value)}
                    style={{ flex: '1 1 200px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  />
                  <Button size="sm" variant="primary" icon={<Plus size={14} />} onClick={handleAddSubpage}>
                    Add Subpage
                  </Button>
                </div>
              </div>

              {/* Section Review Checklist */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
                <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#ffffff', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Review Checklist · Categories & Navigation
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['chk-cat-1', 'chk-cat-2', 'chk-cat-3', 'chk-cat-4'].map(itemId => {
                    const item = project.checklist[itemId];
                    if (!item) return null;
                    return (
                      <div
                        key={itemId}
                        onClick={() => handleToggleChecklistItem(itemId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          background: item.isReviewed ? 'rgba(16, 185, 129, 0.08)' : '#040711',
                          border: item.isReviewed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isReviewed}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: item.isReviewed ? '#10b981' : '#ffffff' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                            {item.description}
                          </div>
                        </div>
                        {item.isReviewed && (
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                            Reviewed {item.reviewedAt ? `on ${item.reviewedAt}` : '✓'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {/* ============================================================
              TAB 2: LOCATION STRATEGY (SINGLE VS MULTI-LOCATION)
             ============================================================ */}
          {activeTab === 'location_strategy' && (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Instructor Strategy Card */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.1) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '14px',
                  padding: '24px',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                  LECTURE 5 WORKFLOW · GEOGRAPHIC OPTIMIZATION
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
                  Single Location Sites vs. Multi-Location Expansion Sites
                </h3>
                <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                  <strong>Single-Location Sites:</strong> When targeting 1 location (e.g. Sydney), optimize every service page for Sydney (Homepage: <em>Digital Marketing Sydney</em>, Web Design page: <em>Web Design Company Sydney</em>, Web Dev: <em>Web Development Sydney</em>, Graphic Design: <em>Graphic Design Services in Sydney</em>).<br />
                  <strong>Multi-Location Sites:</strong> When expanding into new major cities (e.g. Adelaide, Perth, Melbourne), generate dedicated location pages to rank in those areas without duplicating thin pages.
                </p>
              </div>

              {/* Model Selector Card */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: '0 0 16px 0' }}>
                  Select Business Location Model
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div
                    onClick={() => {
                      const updated: OnPageSeoProject = { ...project, locationModel: 'single_location' };
                      handleUpdateProject(updated);
                    }}
                    style={{
                      background: project.locationModel === 'single_location' ? 'rgba(2, 132, 199, 0.15)' : '#040711',
                      border: project.locationModel === 'single_location' ? '2px solid #38bdf8' : '1px solid #1e293b',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff' }}>Single Location Model</span>
                      <MapPin size={18} color="#38bdf8" />
                    </div>
                    <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
                      Business operates in 1 primary city (e.g. Sydney). All individual service pages directly target this location in headings, titles, and body copy.
                    </p>
                  </div>

                  <div
                    onClick={() => {
                      const updated: OnPageSeoProject = { ...project, locationModel: 'multi_location' };
                      handleUpdateProject(updated);
                    }}
                    style={{
                      background: project.locationModel === 'multi_location' ? 'rgba(2, 132, 199, 0.15)' : '#040711',
                      border: project.locationModel === 'multi_location' ? '2px solid #38bdf8' : '1px solid #1e293b',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff' }}>Multi-Location Expansion Model</span>
                      <Building2 size={18} color="#10b981" />
                    </div>
                    <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
                      Business operates in a primary city + surrounds or multiple cities (e.g. Sydney + Perth + Adelaide + Melbourne). Creates dedicated location pages.
                    </p>
                  </div>
                </div>

                {/* Primary & Expansion Cities Input */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                      PRIMARY TARGET LOCATION
                    </label>
                    <input
                      type="text"
                      value={project.primaryLocation}
                      onChange={(e) => {
                        const updated: OnPageSeoProject = { ...project, primaryLocation: e.target.value };
                        handleUpdateProject(updated);
                      }}
                      style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                      EXPANSION CITIES (COMMA-SEPARATED)
                    </label>
                    <input
                      type="text"
                      value={project.expansionCities.join(', ')}
                      onChange={(e) => {
                        const cities = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                        const updated: OnPageSeoProject = { ...project, expansionCities: cities };
                        handleUpdateProject(updated);
                      }}
                      style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                  </div>
                </div>
              </div>

              {/* Section Review Checklist */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
                <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#ffffff', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Review Checklist · Location Architecture Strategy
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['chk-loc-1', 'chk-loc-2'].map(itemId => {
                    const item = project.checklist[itemId];
                    if (!item) return null;
                    return (
                      <div
                        key={itemId}
                        onClick={() => handleToggleChecklistItem(itemId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          background: item.isReviewed ? 'rgba(16, 185, 129, 0.08)' : '#040711',
                          border: item.isReviewed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isReviewed}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: item.isReviewed ? '#10b981' : '#ffffff' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                            {item.description}
                          </div>
                        </div>
                        {item.isReviewed && (
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                            Reviewed {item.reviewedAt ? `on ${item.reviewedAt}` : '✓'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {/* ============================================================
              TAB 3: SITE STRUCTURE & PAGES TO BUILD + TRUST SIGNALS
             ============================================================ */}
          {activeTab === 'site_structure_pages' && (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Instructor Strategy Card */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.1) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '14px',
                  padding: '24px',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                  LECTURES 4 & 5 ARCHITECTURE · SITE BLUEPRINT & TRUST SIGNALS
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
                  Site Architecture: Which Pages to Build & Crucial On-Site Trust Signals
                </h3>
                <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                  <em>"Local SEO depends mostly on trust. Google should know that this is a real business, located here, trustworthy, with real reviews and physical presence."</em><br />
                  A high-ranking local business website requires <strong>separate dedicated pages for every service</strong> (never lump services together), dedicated location hubs, comprehensive <strong>social proof and feedback pages</strong>, and mandatory <strong>legal footer compliance pages</strong> (Privacy Policy, Terms of Use, Disclaimer).
                </p>
              </div>

              {/* Sub-View Switcher & Blueprint Metrics */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '10px', padding: '4px' }}>
                  <button
                    onClick={() => setStructureSubView('pages')}
                    style={{
                      background: structureSubView === 'pages' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
                      color: structureSubView === 'pages' ? '#ffffff' : '#94a3b8',
                      border: 'none',
                      padding: '8px 18px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Layers size={14} />
                    <span>Which Pages to Build ({sitePagesArchitecture.blueprints.filter(b => b.isPlanned).length})</span>
                  </button>

                  <button
                    onClick={() => setStructureSubView('trust_signals')}
                    style={{
                      background: structureSubView === 'trust_signals' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
                      color: structureSubView === 'trust_signals' ? '#ffffff' : '#94a3b8',
                      border: 'none',
                      padding: '8px 18px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <ShieldCheck size={14} />
                    <span>On-Site Trust Signals ({sitePagesArchitecture.trustSignals.filter(t => t.isImplemented).length}/{sitePagesArchitecture.trustSignals.length})</span>
                  </button>
                </div>

                {/* Quick Action */}
                {structureSubView === 'pages' && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Plus size={14} />}
                    onClick={() => setShowAddPageModal(true)}
                  >
                    Add Custom Page Blueprint
                  </Button>
                )}
              </div>

              {/* VIEW 1: WHICH PAGES TO BUILD */}
              {structureSubView === 'pages' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Category Filter Chips */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {[
                      { id: 'all', label: 'All Pages' },
                      { id: 'core', label: 'Core Pages (Homepage, About, Contact)' },
                      { id: 'service', label: 'Dedicated Service Pages' },
                      { id: 'location', label: 'Location Hubs' },
                      { id: 'trust_social', label: 'Trust & Social Proof' },
                      { id: 'authority', label: 'Blog & Authority' },
                      { id: 'legal', label: 'Legal & Compliance Footers' },
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setPageCategoryFilter(tab.id as any)}
                        style={{
                          background: pageCategoryFilter === tab.id ? 'rgba(56, 189, 248, 0.15)' : '#090e1a',
                          border: pageCategoryFilter === tab.id ? '1px solid #38bdf8' : '1px solid #1e293b',
                          color: pageCategoryFilter === tab.id ? '#38bdf8' : '#94a3b8',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Page Cards Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px' }}>
                    {sitePagesArchitecture.blueprints
                      .filter(bp => pageCategoryFilter === 'all' || bp.category === pageCategoryFilter)
                      .map(bp => {
                        const getCategoryColor = (cat: string) => {
                          switch (cat) {
                            case 'core': return { bg: 'rgba(56, 189, 248, 0.15)', text: '#38bdf8', border: 'rgba(56, 189, 248, 0.3)' };
                            case 'service': return { bg: 'rgba(168, 85, 247, 0.15)', text: '#c084fc', border: 'rgba(168, 85, 247, 0.3)' };
                            case 'location': return { bg: 'rgba(234, 179, 8, 0.15)', text: '#facc15', border: 'rgba(234, 179, 8, 0.3)' };
                            case 'trust_social': return { bg: 'rgba(16, 185, 129, 0.15)', text: '#34d399', border: 'rgba(16, 185, 129, 0.3)' };
                            case 'authority': return { bg: 'rgba(99, 102, 241, 0.15)', text: '#818cf8', border: 'rgba(99, 102, 241, 0.3)' };
                            case 'legal': return { bg: 'rgba(239, 68, 68, 0.15)', text: '#f87171', border: 'rgba(239, 68, 68, 0.3)' };
                            default: return { bg: 'rgba(100, 116, 139, 0.15)', text: '#94a3b8', border: 'rgba(100, 116, 139, 0.3)' };
                          }
                        };
                        const catColor = getCategoryColor(bp.category);

                        return (
                          <div
                            key={bp.id}
                            style={{
                              background: bp.isBuilt ? 'rgba(16, 185, 129, 0.04)' : '#090e1a',
                              border: bp.isBuilt ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                              borderRadius: '14px',
                              padding: '20px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <span
                                  style={{
                                    fontSize: '10.5px',
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.6px',
                                    background: catColor.bg,
                                    color: catColor.text,
                                    border: `1px solid ${catColor.border}`,
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                  }}
                                >
                                  {bp.category.replace('_', ' ')}
                                </span>

                                <span
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    color: bp.isBuilt ? '#10b981' : bp.isPlanned ? '#38bdf8' : '#64748b',
                                  }}
                                >
                                  {bp.isBuilt ? '✓ BUILT & LIVE' : bp.isPlanned ? 'PLANNED' : 'EXCLUDED'}
                                </span>
                              </div>

                              <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0' }}>
                                {bp.title}
                              </h4>

                              <div style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: '#38bdf8', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>{project.websiteUrl.replace(/\/$/, '')}{bp.targetUrl}</span>
                              </div>

                              <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: '0 0 16px 0', lineHeight: 1.5 }}>
                                {bp.purpose}
                              </p>

                              {/* Required Elements */}
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
                                  REQUIRED ON-PAGE ELEMENTS
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {bp.keyElements.map((el, i) => (
                                    <span
                                      key={i}
                                      style={{
                                        fontSize: '11px',
                                        background: '#040711',
                                        border: '1px solid #1e293b',
                                        color: '#cbd5e1',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                      }}
                                    >
                                      {el}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Status Toggles Bar */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '14px', borderTop: '1px solid #1e293b' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: bp.isPlanned ? '#38bdf8' : '#64748b' }}>
                                <input
                                  type="checkbox"
                                  checked={bp.isPlanned}
                                  onChange={() => handleTogglePagePlanned(bp.id)}
                                  style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#0284c7' }}
                                />
                                <span>Include in Architecture</span>
                              </label>

                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: bp.isBuilt ? '#10b981' : '#64748b', fontWeight: 600 }}>
                                <input
                                  type="checkbox"
                                  checked={bp.isBuilt}
                                  onChange={() => handleTogglePageBuilt(bp.id)}
                                  style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#10b981' }}
                                />
                                <span>Built & Live ✓</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Add Custom Blueprint Modal */}
                  {showAddPageModal && (
                    <div
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 100,
                        padding: '16px',
                      }}
                    >
                      <div
                        style={{
                          background: '#090e1a',
                          border: '1px solid #1e293b',
                          borderRadius: '16px',
                          padding: '24px',
                          maxWidth: '500px',
                          width: '100%',
                        }}
                      >
                        <h4 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 16px 0' }}>
                          Add Custom Page Blueprint
                        </h4>
                        <form onSubmit={handleAddCustomPageBlueprint} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                              PAGE TITLE
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. Emergency Plumbing 24/7"
                              value={newPageTitle}
                              onChange={(e) => setNewPageTitle(e.target.value)}
                              required
                              style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                            />
                          </div>

                          <div>
                            <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                              TARGET URL PATH
                            </label>
                            <input
                              type="text"
                              placeholder="/emergency-plumbing"
                              value={newPageUrl}
                              onChange={(e) => setNewPageUrl(e.target.value)}
                              style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                            />
                          </div>

                          <div>
                            <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                              PAGE CATEGORY
                            </label>
                            <select
                              value={newPageCategory}
                              onChange={(e) => setNewPageCategory(e.target.value as any)}
                              style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                            >
                              <option value="core">Core Page</option>
                              <option value="service">Dedicated Service Page</option>
                              <option value="location">Location Hub</option>
                              <option value="trust_social">Trust & Social Proof</option>
                              <option value="authority">Authority / Blog</option>
                              <option value="legal">Legal & Compliance</option>
                            </select>
                          </div>

                          <div>
                            <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                              SEO & TRUST PURPOSE
                            </label>
                            <textarea
                              rows={3}
                              placeholder="Describe why this page is created and how it satisfies customer search intent..."
                              value={newPagePurpose}
                              onChange={(e) => setNewPagePurpose(e.target.value)}
                              style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                            />
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                            <Button type="button" variant="secondary" onClick={() => setShowAddPageModal(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" variant="primary" icon={<Plus size={14} />}>
                              Save Blueprint
                            </Button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* VIEW 2: TRUST SIGNALS AUDIT */}
              {structureSubView === 'trust_signals' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  
                  <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0' }}>
                      On-Site Trust Signals Audit Checklist
                    </h4>
                    <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
                      Lecture 5 emphasizes that physical trust indicators directly drive Google local rankings and prevent algorithmic red-flags. Verify each signal is live on the client website.
                    </p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px' }}>
                    {sitePagesArchitecture.trustSignals.map(ts => (
                      <div
                        key={ts.id}
                        onClick={() => handleToggleTrustSignal(ts.id)}
                        style={{
                          background: ts.isImplemented ? 'rgba(16, 185, 129, 0.05)' : '#090e1a',
                          border: ts.isImplemented ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid #1e293b',
                          borderRadius: '14px',
                          padding: '20px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <span
                              style={{
                                fontSize: '10.5px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '0.6px',
                                background: ts.isImplemented ? 'rgba(16, 185, 129, 0.15)' : 'rgba(30, 41, 59, 0.5)',
                                color: ts.isImplemented ? '#10b981' : '#94a3b8',
                                padding: '2px 8px',
                                borderRadius: '4px',
                              }}
                            >
                              {ts.category.replace('_', ' ')}
                            </span>

                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: ts.isImplemented ? '#10b981' : '#f59e0b',
                              }}
                            >
                              {ts.isImplemented ? 'IMPLEMENTED ✓' : 'ACTION REQUIRED'}
                            </span>
                          </div>

                          <h4 style={{ fontSize: '15.5px', fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0' }}>
                            {ts.name}
                          </h4>

                          <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                            {ts.description}
                          </p>
                        </div>

                        <div style={{ paddingTop: '12px', borderTop: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                            Location: <strong style={{ color: '#cbd5e1' }}>{ts.locationOnSite}</strong>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input
                              type="checkbox"
                              checked={ts.isImplemented}
                              onChange={() => {}}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 700, color: ts.isImplemented ? '#10b981' : '#94a3b8' }}>
                              Verified
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              )}

              {/* Section Review Checklist */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
                <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#ffffff', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Review Checklist · Site Blueprint & Trust Signals Architecture
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['chk-str-1', 'chk-str-2', 'chk-str-3', 'chk-str-4', 'chk-trs-1'].map(itemId => {
                    const item = mergedChecklist[itemId];
                    if (!item) return null;
                    return (
                      <div
                        key={itemId}
                        onClick={() => handleToggleChecklistItem(itemId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          background: item.isReviewed ? 'rgba(16, 185, 129, 0.08)' : '#040711',
                          border: item.isReviewed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isReviewed}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: item.isReviewed ? '#10b981' : '#ffffff' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                            {item.description}
                          </div>
                        </div>
                        {item.isReviewed && (
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                            Reviewed {item.reviewedAt ? `on ${item.reviewedAt}` : '✓'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {/* ============================================================
              TAB 4: CONTENT HIERARCHY & SEARCH VOLUME SCALING
             ============================================================ */}
          {activeTab === 'content_hierarchy' && (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Instructor Strategy Card */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.1) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '14px',
                  padding: '24px',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                  LECTURE 5 WORKFLOW · MULTI-SERVICE LOCATION PAGE HIERARCHY
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
                  Heading & Content Word Count Scaling by Search Volume
                </h3>
                <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                  When expanding into a city (e.g. Perth) where the client offers 5 services: instead of creating 5 thin pages, the instructor consolidates them onto a comprehensive location page (e.g. <code>/perth</code>).<br />
                  • <strong>Highest Search Volume & Competition</strong> (e.g. <em>Web Design Perth</em>, SV = 500) → <strong>H1 Heading</strong>, 300–400 (or 400–500) words, keyword in H1 + 2x in body.<br />
                  • <strong>Medium Search Volume</strong> (e.g. <em>Web Development Perth</em>, SV = 300) → <strong>H2 Heading</strong>, 250–300 words, keyword in H2 + 1–2x in body.<br />
                  • <strong>Lower Search Volume</strong> (e.g. <em>Graphic Design Perth</em>, SV = 200) → <strong>H2 Heading</strong>, 200–300 words, keyword in H2 + 1x in body.
                </p>
              </div>

              {/* Location Page Content Architecture Calculator */}
              {currentPlan && (
                <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: '0 0 4px 0' }}>
                        {currentPlan.targetCity} Multi-Service Location Page Content Plan
                      </h4>
                      <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                        Target URL: <code>{currentPlan.locationPageUrl}</code> · Scaled according to search volume & competition.
                      </p>
                    </div>

                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', padding: '4px 10px', borderRadius: '6px' }}>
                      Single H1 Architecture Verified
                    </span>
                  </div>

                  {/* Primary H1 Service Card */}
                  <div
                    style={{
                      background: 'rgba(2, 132, 199, 0.08)',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      borderRadius: '12px',
                      padding: '18px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 900, color: '#ffffff', background: '#0284c7', padding: '3px 8px', borderRadius: '4px' }}>
                          TAG: H1
                        </span>
                        <h5 style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                          {currentPlan.h1Service.serviceName} ({currentPlan.h1Service.targetKeyword})
                        </h5>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>Search Volume: <strong style={{ color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>{currentPlan.h1Service.searchVolume}/mo</strong> (Highest)</span>
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>Recommended: <strong style={{ color: '#10b981', fontFamily: 'var(--font-mono)' }}>{currentPlan.h1Service.recommendedWordCount} words</strong></span>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '12px' }}>
                      <div style={{ background: '#040711', padding: '10px 14px', borderRadius: '8px', border: '1px solid #1e293b' }}>
                        <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>KEYWORD IN HEADING</span>
                        <span style={{ color: '#10b981', fontWeight: 700 }}>✓ Included 1x in H1</span>
                      </div>
                      <div style={{ background: '#040711', padding: '10px 14px', borderRadius: '8px', border: '1px solid #1e293b' }}>
                        <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>KEYWORD IN CONTENT BODY</span>
                        <span style={{ color: '#10b981', fontWeight: 700 }}>✓ Included 2x naturally</span>
                      </div>
                      <div style={{ background: '#040711', padding: '10px 14px', borderRadius: '8px', border: '1px solid #1e293b' }}>
                        <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>COMPETITION LEVEL</span>
                        <span style={{ color: '#f59e0b', fontWeight: 700 }}>High Competition</span>
                      </div>
                    </div>
                  </div>

                  {/* Secondary H2 Services Stack */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                    {currentPlan.secondaryServices.map((srv) => (
                      <div
                        key={srv.id}
                        style={{
                          background: '#040711',
                          border: '1px solid #1e293b',
                          borderRadius: '12px',
                          padding: '16px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 900, color: '#94a3b8', background: '#1e293b', padding: '3px 8px', borderRadius: '4px' }}>
                              TAG: H2
                            </span>
                            <h5 style={{ fontSize: '15px', fontWeight: 700, color: '#ffffff', margin: 0 }}>
                              {srv.serviceName} ({srv.targetKeyword})
                            </h5>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Search Volume: <strong style={{ color: '#ffffff', fontFamily: 'var(--font-mono)' }}>{srv.searchVolume}/mo</strong></span>
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Recommended: <strong style={{ color: '#ffffff', fontFamily: 'var(--font-mono)' }}>{srv.recommendedWordCount} words</strong></span>
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Density: <strong style={{ color: '#10b981' }}>{srv.keywordOccurrencesInContent}x in body</strong></span>
                          </div>
                        </div>

                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {srv.notes}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Service to Location Plan */}
                  <div style={{ display: 'flex', gap: '12px', background: '#040711', padding: '14px', borderRadius: '10px', border: '1px solid #1e293b', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      placeholder="Service Name (e.g. SEO Audit Services)"
                      value={newSrvName}
                      onChange={(e) => setNewSrvName(e.target.value)}
                      style={{ flex: '2 1 200px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                    <input
                      type="number"
                      placeholder="Search Volume"
                      value={newSrvVolume}
                      onChange={(e) => setNewSrvVolume(Number(e.target.value))}
                      style={{ flex: '1 1 120px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                    <input
                      type="text"
                      placeholder="Keyword (optional)"
                      value={newSrvKeyword}
                      onChange={(e) => setNewSrvKeyword(e.target.value)}
                      style={{ flex: '2 1 200px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                    <Button size="sm" variant="primary" icon={<Plus size={14} />} onClick={handleAddSecondaryService}>
                      Add Service Section
                    </Button>
                  </div>
                </div>
              )}

              {/* Section Review Checklist */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
                <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#ffffff', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Review Checklist · Content Hierarchy & Search Volume Scaling
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['chk-cnt-1', 'chk-cnt-2', 'chk-cnt-3', 'chk-cnt-4'].map(itemId => {
                    const item = project.checklist[itemId];
                    if (!item) return null;
                    return (
                      <div
                        key={itemId}
                        onClick={() => handleToggleChecklistItem(itemId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          background: item.isReviewed ? 'rgba(16, 185, 129, 0.08)' : '#040711',
                          border: item.isReviewed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isReviewed}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: item.isReviewed ? '#10b981' : '#ffffff' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                            {item.description}
                          </div>
                        </div>
                        {item.isReviewed && (
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                            Reviewed {item.reviewedAt ? `on ${item.reviewedAt}` : '✓'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {/* ============================================================
              TAB 4: PERMALINKS & 301 REDIRECTION MANAGER
             ============================================================ */}
          {activeTab === 'permalinks_redirects' && (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Instructor Strategy Card */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.1) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '14px',
                  padding: '24px',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                  LECTURE 5 WORKFLOW · URL ARCHITECTURE & LINK EQUITY
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
                  Clean Permalinks, Existing URL Preservation & 301 Redirect Rules
                </h3>
                <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                  The instructor emphasizes two golden rules:<br />
                  1. <strong>Do Not Touch Ranking URLs:</strong> If a client already has existing location pages (e.g. <code>website.com/perth</code>) that are ranking, do NOT touch or change the permalink just to add a keyword. Keyword in URL is a ranking factor, but not the only one.<br />
                  2. <strong>Mandatory 301 Permanent Redirects:</strong> If existing permalinks are extremely messy/long and MUST be updated, change them to clean URLs and <strong>ALWAYS</strong> configure 301 permanent redirects from the old URL to the new URL (via Yoast Advanced Settings, Simple 301 Redirects plugin, or Really Simple SSL) to preserve search equity.
                </p>
              </div>

              {/* Permalink Audits & Redirect Plan */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: '0 0 16px 0' }}>
                  Permalink Audit & 301 Redirection Table
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                  {project.permalinkAudits.map((prm) => (
                    <div
                      key={prm.id}
                      style={{
                        background: '#040711',
                        border: '1px solid #1e293b',
                        borderRadius: '12px',
                        padding: '16px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>
                          {prm.pageTitle}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#f59e0b', background: 'rgba(245, 158, 11, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
                            301 Permanent
                          </span>
                          <span style={{ fontSize: '11px', color: '#94a3b8', background: '#1e293b', padding: '2px 8px', borderRadius: '4px' }}>
                            Tool: {prm.redirectTool.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', fontSize: '12px', fontFamily: 'var(--font-mono)' }}>
                        <div style={{ background: 'rgba(244, 63, 94, 0.08)', border: '1px solid rgba(244, 63, 94, 0.2)', padding: '8px 12px', borderRadius: '6px' }}>
                          <span style={{ color: '#f87171', display: 'block', fontSize: '10px', textTransform: 'uppercase', marginBottom: '2px' }}>OLD MESSY URL</span>
                          <span style={{ color: '#e2e8f0', wordBreak: 'break-all' }}>{prm.oldUrl}</span>
                        </div>

                        <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)', padding: '8px 12px', borderRadius: '6px' }}>
                          <span style={{ color: '#34d399', display: 'block', fontSize: '10px', textTransform: 'uppercase', marginBottom: '2px' }}>TARGET CLEAN URL</span>
                          <span style={{ color: '#e2e8f0', wordBreak: 'break-all' }}>{prm.newUrl}</span>
                        </div>
                      </div>

                      {prm.notes && (
                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '10px' }}>
                          Note: {prm.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add Permalink Audit Row */}
                <div style={{ display: 'flex', gap: '12px', background: '#040711', padding: '14px', borderRadius: '10px', border: '1px solid #1e293b', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Page Name (e.g. Adelaide Hub)"
                    value={newPrmTitle}
                    onChange={(e) => setNewPrmTitle(e.target.value)}
                    style={{ flex: '1 1 180px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  />
                  <input
                    type="text"
                    placeholder="Old messy URL"
                    value={newPrmOldUrl}
                    onChange={(e) => setNewPrmOldUrl(e.target.value)}
                    style={{ flex: '2 1 220px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  />
                  <input
                    type="text"
                    placeholder="New clean target URL"
                    value={newPrmNewUrl}
                    onChange={(e) => setNewPrmNewUrl(e.target.value)}
                    style={{ flex: '2 1 220px', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                  />
                  <Button size="sm" variant="primary" icon={<Plus size={14} />} onClick={handleAddPermalink}>
                    Add 301 Mapping
                  </Button>
                </div>
              </div>

              {/* Section Review Checklist */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
                <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#ffffff', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Review Checklist · Permalinks & 301 Redirections
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['chk-prm-1', 'chk-prm-2', 'chk-prm-3'].map(itemId => {
                    const item = project.checklist[itemId];
                    if (!item) return null;
                    return (
                      <div
                        key={itemId}
                        onClick={() => handleToggleChecklistItem(itemId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          background: item.isReviewed ? 'rgba(16, 185, 129, 0.08)' : '#040711',
                          border: item.isReviewed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isReviewed}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: item.isReviewed ? '#10b981' : '#ffffff' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                            {item.description}
                          </div>
                        </div>
                        {item.isReviewed && (
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                            Reviewed {item.reviewedAt ? `on ${item.reviewedAt}` : '✓'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {/* ============================================================
              TAB 5: NAP CONSISTENCY & GMB TRUST
             ============================================================ */}
          {activeTab === 'nap_gmb' && (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Instructor Strategy Card */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.1) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '14px',
                  padding: '24px',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                  LECTURE 5 WORKFLOW · NAP & LOCAL GOOGLE TRUST
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
                  Exact NAP Consistency & Google Business Profile (GMB) Verification
                </h3>
                <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                  Local SEO rankings depend heavily on <strong>TRUST</strong>. Google needs confidence that this is a real, legitimate physical business located in the target city.<br />
                  • <strong>Exact Business Name:</strong> Must be 100% identical across your website, Google Business Profile, Facebook, YellowPages, Yelp, and citations. <em>"Not even a comma difference!"</em> Adding "LTD" in one place and leaving it out elsewhere confuses Google and can cause GMB suspension.<br />
                  • <strong>Physical Address & Exact Zip Code:</strong> Google uses the exact Zip Code (e.g. 66000 in Multan) to map physical existence and ranking radius.<br />
                  • <strong>Local Phone Number:</strong> Use a real local number with regional area code.
                </p>
              </div>

              {/* Interactive NAP Profile Validator */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: '0 0 16px 0' }}>
                  NAP Data Profile Validator
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                      EXACT BUSINESS NAME (ZERO VARIATION TOLERANCE)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Apex Digital Solutions"
                      value={project.napProfile.exactBusinessName}
                      onChange={(e) => {
                        const updated: OnPageSeoProject = {
                          ...project,
                          napProfile: { ...project.napProfile, exactBusinessName: e.target.value },
                        };
                        handleUpdateProject(updated);
                      }}
                      style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                      LOCAL PHONE NUMBER (WITH AREA CODE)
                    </label>
                    <input
                      type="text"
                      placeholder="+61 2 8000 1234 or +92 61 1234567"
                      value={project.napProfile.phoneNumber}
                      onChange={(e) => {
                        const updated: OnPageSeoProject = {
                          ...project,
                          napProfile: { ...project.napProfile, phoneNumber: e.target.value },
                        };
                        handleUpdateProject(updated);
                      }}
                      style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                      STREET ADDRESS
                    </label>
                    <input
                      type="text"
                      placeholder="Suite 101, 123 Market Street"
                      value={project.napProfile.streetAddress}
                      onChange={(e) => {
                        const updated: OnPageSeoProject = {
                          ...project,
                          napProfile: { ...project.napProfile, streetAddress: e.target.value },
                        };
                        handleUpdateProject(updated);
                      }}
                      style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                        CITY / STATE
                      </label>
                      <input
                        type="text"
                        placeholder="Sydney, NSW"
                        value={project.napProfile.city}
                        onChange={(e) => {
                          const updated: OnPageSeoProject = {
                            ...project,
                            napProfile: { ...project.napProfile, city: e.target.value },
                          };
                          handleUpdateProject(updated);
                        }}
                        style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                        POSTAL / ZIP CODE
                      </label>
                      <input
                        type="text"
                        placeholder="2000 or 66000"
                        value={project.napProfile.zipCode}
                        onChange={(e) => {
                          const updated: OnPageSeoProject = {
                            ...project,
                            napProfile: { ...project.napProfile, zipCode: e.target.value },
                          };
                          handleUpdateProject(updated);
                        }}
                        style={{ width: '100%', background: '#040711', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#ffffff', outline: 'none' }}
                      />
                    </div>
                  </div>
                </div>

                {/* NAP Verification Flags */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  <div
                    onClick={() => {
                      const updated: OnPageSeoProject = {
                        ...project,
                        napProfile: { ...project.napProfile, gmbVerified: !project.napProfile.gmbVerified },
                      };
                      handleUpdateProject(updated);
                    }}
                    style={{
                      background: project.napProfile.gmbVerified ? 'rgba(16, 185, 129, 0.1)' : '#040711',
                      border: project.napProfile.gmbVerified ? '1px solid #10b981' : '1px solid #1e293b',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 700, color: project.napProfile.gmbVerified ? '#10b981' : '#ffffff' }}>
                      {project.napProfile.gmbVerified ? '✓ GMB Match' : 'GMB Match'}
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>Google Business Profile</div>
                  </div>

                  <div
                    onClick={() => {
                      const updated: OnPageSeoProject = {
                        ...project,
                        napProfile: { ...project.napProfile, websiteFooterVerified: !project.napProfile.websiteFooterVerified },
                      };
                      handleUpdateProject(updated);
                    }}
                    style={{
                      background: project.napProfile.websiteFooterVerified ? 'rgba(16, 185, 129, 0.1)' : '#040711',
                      border: project.napProfile.websiteFooterVerified ? '1px solid #10b981' : '1px solid #1e293b',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 700, color: project.napProfile.websiteFooterVerified ? '#10b981' : '#ffffff' }}>
                      {project.napProfile.websiteFooterVerified ? '✓ Site Footer Match' : 'Site Footer Match'}
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>Website HTML Footer</div>
                  </div>

                  <div
                    onClick={() => {
                      const updated: OnPageSeoProject = {
                        ...project,
                        napProfile: { ...project.napProfile, socialProfilesMatch: !project.napProfile.socialProfilesMatch },
                      };
                      handleUpdateProject(updated);
                    }}
                    style={{
                      background: project.napProfile.socialProfilesMatch ? 'rgba(16, 185, 129, 0.1)' : '#040711',
                      border: project.napProfile.socialProfilesMatch ? '1px solid #10b981' : '1px solid #1e293b',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 700, color: project.napProfile.socialProfilesMatch ? '#10b981' : '#ffffff' }}>
                      {project.napProfile.socialProfilesMatch ? '✓ Social Match' : 'Social Match'}
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>Facebook / Socials</div>
                  </div>

                  <div
                    onClick={() => {
                      const updated: OnPageSeoProject = {
                        ...project,
                        napProfile: { ...project.napProfile, localDirectoriesMatch: !project.napProfile.localDirectoriesMatch },
                      };
                      handleUpdateProject(updated);
                    }}
                    style={{
                      background: project.napProfile.localDirectoriesMatch ? 'rgba(16, 185, 129, 0.1)' : '#040711',
                      border: project.napProfile.localDirectoriesMatch ? '1px solid #10b981' : '1px solid #1e293b',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 700, color: project.napProfile.localDirectoriesMatch ? '#10b981' : '#ffffff' }}>
                      {project.napProfile.localDirectoriesMatch ? '✓ Directory Match' : 'Directory Match'}
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>Yelp / YellowPages / Citations</div>
                  </div>
                </div>
              </div>

              {/* Section Review Checklist */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
                <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#ffffff', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Review Checklist · NAP Consistency & Local Google Trust
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['chk-nap-1', 'chk-nap-2', 'chk-nap-3', 'chk-nap-4'].map(itemId => {
                    const item = project.checklist[itemId];
                    if (!item) return null;
                    return (
                      <div
                        key={itemId}
                        onClick={() => handleToggleChecklistItem(itemId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          background: item.isReviewed ? 'rgba(16, 185, 129, 0.08)' : '#040711',
                          border: item.isReviewed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isReviewed}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: item.isReviewed ? '#10b981' : '#ffffff' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                            {item.description}
                          </div>
                        </div>
                        {item.isReviewed && (
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                            Reviewed {item.reviewedAt ? `on ${item.reviewedAt}` : '✓'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {/* ============================================================
              TAB 6: MASTER REVIEW CHECKLIST & PIPELINE SIGN-OFF
             ============================================================ */}
          {activeTab === 'master_checklist' && (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Review Summary Hero */}
              <div
                style={{
                  background: progressPct === 100
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(9, 14, 26, 0.95) 100%)'
                    : 'linear-gradient(135deg, rgba(2, 132, 199, 0.12) 0%, rgba(9, 14, 26, 0.95) 100%)',
                  border: progressPct === 100 ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '16px',
                  padding: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '24px',
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 800, color: progressPct === 100 ? '#10b981' : '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                      ON-PAGE SEO AUDIT & PIPELINE HANDOFF
                    </span>
                  </div>
                  <h3 style={{ fontSize: '22px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
                    {progressPct === 100 ? 'All 18 Implementation Criteria Reviewed' : `${reviewedItemsCount} of ${totalItemsCount} Implementation Items Reviewed`}
                  </h3>
                  <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, maxWidth: '640px', lineHeight: 1.5 }}>
                    This walkthrough checklist ensures all technical on-page requirements from Lecture 5 are verified before connecting this module to the automated SEO pipeline and final client reports.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                  <div style={{ fontSize: '36px', fontWeight: 900, color: progressPct === 100 ? '#10b981' : '#ffffff', fontFamily: 'var(--font-mono)' }}>
                    {progressPct}%
                  </div>
                  <Button
                    size="md"
                    variant={progressPct === 100 ? 'success' : 'primary'}
                    icon={<CheckSquare size={16} />}
                    onClick={() => {
                      const allChecked: Record<string, OnPageChecklistReviewItem> = {};
                      Object.keys(project.checklist).forEach(k => {
                        allChecked[k] = { ...project.checklist[k], isReviewed: true, reviewedAt: new Date().toLocaleDateString() };
                      });
                      const updated = { ...project, checklist: allChecked, status: 'reviewed' as const };
                      handleUpdateProject(updated);
                    }}
                  >
                    Mark All As Reviewed ✓
                  </Button>
                </div>
              </div>

              {/* Master Checklist Grouped by Category */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', margin: '0 0 16px 0' }}>
                  Full Review Checklist
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {allChecklistItems.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleToggleChecklistItem(item.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: item.isReviewed ? 'rgba(16, 185, 129, 0.08)' : '#040711',
                        border: item.isReviewed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #1e293b',
                        borderRadius: '10px',
                        padding: '14px 18px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                        <input
                          type="checkbox"
                          checked={item.isReviewed}
                          onChange={() => {}}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', accentColor: '#10b981' }}
                        />
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: item.isReviewed ? '#10b981' : '#ffffff', marginBottom: '3px' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '12.5px', color: '#94a3b8' }}>
                            {item.description}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: item.isReviewed ? '#10b981' : '#64748b',
                            background: item.isReviewed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(30, 41, 59, 0.4)',
                            padding: '3px 10px',
                            borderRadius: '6px',
                          }}
                        >
                          {item.isReviewed ? 'REVIEWED ✓' : 'PENDING'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Executive Summary & Pipeline Handoff */}
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', margin: '0 0 12px 0' }}>
                  On-Page SEO Executive Summary
                </h4>
                <textarea
                  rows={4}
                  value={project.executiveSummary || ''}
                  onChange={(e) => {
                    const updated: OnPageSeoProject = { ...project, executiveSummary: e.target.value };
                    handleUpdateProject(updated);
                  }}
                  style={{
                    width: '100%',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '13.5px',
                    color: '#ffffff',
                    lineHeight: 1.6,
                    outline: 'none',
                    marginBottom: '16px',
                  }}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Ready for pipeline unification with Audits & Keyword Research
                  </span>
                  <Button
                    size="md"
                    variant="primary"
                    icon={<Copy size={15} />}
                    onClick={() => copyToClipboard(project.executiveSummary || '', 'summary')}
                  >
                    {copiedText === 'summary' ? 'Copied Summary!' : 'Copy Summary'}
                  </Button>
                </div>
              </div>

            </div>
          )}

        </div>
      </div>

    </div>
  );
};
